import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  doc,
  runTransaction,
  increment,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Export db for components that need it
export { db };

import { generateOrderNumber, generateOrderDocId, generateDraftOrderNumber } from '../utils/orderUtils';

// Products service
export const productService = {
  async getProducts() {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('isActive', '==', true),
        where('isStandaloneProduct', '!=', false)
      );
      const querySnapshot = await getDocs(q);

      const products = {};
      querySnapshot.forEach((doc) => {
        products[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });

      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  async getProductsByCategory(category) {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('category', '==', category),
        where('isActive', '==', true),
        where('isStandaloneProduct', '!=', false)
      );
      const querySnapshot = await getDocs(q);

      const products = {};
      querySnapshot.forEach((doc) => {
        products[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });

      return products;
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }
};

// Order service
// Function to update demo booking status
async function updateDemoBookingStatus(demoId, status) {
  if (!demoId) return;

  try {
    const demoRef = doc(db, 'demo_bookings', demoId);
    await updateDoc(demoRef, {
      'payload.status': status,
      'payload.updatedAt': serverTimestamp()
    });
    console.log(`Demo booking ${demoId} status updated to ${status}`);
  } catch (error) {
    console.error('Error updating demo booking status:', error);
    // Don't fail the order if demo update fails
  }
}

export const orderService = {
  async findCustomerUpgradeEligibility(email, mobile) {
    try {
      const ordersRef = collection(db, 'orders');
      let orders = [];

      // Clean up inputs
      const searchEmail = email?.toLowerCase().trim();
      const searchMobile = mobile?.trim();

      // Query by email
      if (searchEmail) {
        const emailQ = query(ordersRef, where('customer.email', '==', searchEmail));
        const emailSnap = await getDocs(emailQ);
        emailSnap.forEach(doc => orders.push({ id: doc.id, ...doc.data() }));
      }

      // Query by mobile
      if (searchMobile) {
        const mobileQ = query(ordersRef, where('customer.mobile', '==', searchMobile));
        const mobileSnap = await getDocs(mobileQ);
        mobileSnap.forEach(doc => {
          if (!orders.some(o => o.id === doc.id)) {
            orders.push({ id: doc.id, ...doc.data() });
          }
        });
      }

      // Filter for orders containing "SET" products AND NOT already upgraded
      const setOrders = orders.filter(order => 
        !order.has_been_upgraded &&
        (order.items || []).some(item => {
          const sku = (item.sku || '').toUpperCase();
          return sku.includes('SET');
        })
      );

      if (setOrders.length === 0) {
        return { eligible: false, message: 'No previous SET purchases found.' };
      }

      // Find highest tier set
      let highestSet = null;
      let lowerSetSku = null;
      let sourceOrder = null;

      setOrders.forEach(order => {
        (order.items || []).forEach(item => {
          const sku = (item.sku || '').toUpperCase();
          if (sku.includes('SET')) {
            const priceLocal = parseFloat(item.unit_cost_local_currency || 0);
            if (!highestSet || priceLocal > (highestSet.priceLocal || 0)) {
              // Store the previous highest's SKU as the lower set
              if (highestSet) {
                lowerSetSku = highestSet.sku;
              }
              highestSet = {
                sku: item.sku,
                name: item.name,
                priceLocal: priceLocal,
                purchasedAt: order.created_at?.toDate ? order.created_at.toDate().toISOString() : order.timestamp || null,
                agentName: order.agentName || order.agent_id || 'Unknown'
              };
              sourceOrder = order;
            }
          }
        });
      });

      // Calculate confidence score
      let confidence = 0;
      const matchedOrder = sourceOrder;
      const emailMatch = matchedOrder.customer?.email?.toLowerCase().trim() === searchEmail;
      const mobileMatch = matchedOrder.customer?.mobile?.trim() === searchMobile;

      if (emailMatch && mobileMatch) confidence = 100;
      else if (mobileMatch) confidence = 75;
      else if (emailMatch) confidence = 50;

      return {
        eligible: true,
        highestSet: {
          ...highestSet,
          orderCode: matchedOrder.code,
          orderId: matchedOrder.id || matchedOrder.orderDocRef
        },
        lowerSetSku: lowerSetSku,
        confidence: confidence,
        customerName: matchedOrder.customer?.name
      };
    } catch (error) {
      console.error('Error finding upgrade eligibility:', error);
      return { eligible: false, message: 'Search failed.' };
    }
  },

  async createOrder(orderData, agentId, maxRetries = 3) {
    try {
      const now = new Date();
      const timestamp = now.toISOString();

      const requiredFields = ['customer', 'items', 'currency'];
      const missingFields = requiredFields.filter(field => !orderData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const currency = orderData.currency || 'AED';
      const countryInfo = {
        'AED': { name: 'United Arab Emirates', country_iso: 'ARE', code: 'AED', flag: '🇦🇪', rate: '3.670', symbol: 'AED' },
        'BHD': { name: 'Bahrain', country_iso: 'BHR', code: 'BHD', flag: '🇧🇭', rate: '0.380', symbol: 'BHD' },
        'KWD': { name: 'Kuwait', country_iso: 'KWT', code: 'KWD', flag: '🇰🇼', rate: '0.310', symbol: 'KWD' },
        'OMR': { name: 'Oman', country_iso: 'OMN', code: 'OMR', flag: '🇴🇲', rate: '0.380', symbol: 'OMR' },
        'QAR': { name: 'Qatar', country_iso: 'QAT', code: 'QAR', flag: '🇶🇦', rate: '3.640', symbol: 'QAR' },
        'SAR': { name: 'Saudi Arabia', country_iso: 'SAU', code: 'SAR', flag: '🇸🇦', rate: '3.750', symbol: 'SAR' },
        'USD': { name: 'Philippines', country_iso: 'PHL', code: 'USD', flag: '🇵🇭', rate: '1.000', symbol: '$' }
      };

      const currentCountry = countryInfo[currency] || countryInfo['AED'];
      const orderNumber = await generateOrderNumber(currentCountry.country_iso);
      const orderDocId = generateOrderDocId(orderNumber);

      const items = (orderData.items || []).map(item => {
        const localPrice = item.price !== undefined ? parseFloat(item.price) : (item.prices ? parseFloat(item.prices[currency]) : 0);
        const usdPrice = item.prices ? parseFloat(item.prices.USD) : localPrice;
        const quantity = parseInt(item.quantity) || 1;

        return {
          name: String(item.name || ''),
          sku: String(item.id || ''),
          quantity: String(quantity),
          unit_cost_usd: usdPrice.toFixed(2),
          unit_cost_local_currency: localPrice.toFixed(2),
          subtotal_usd: (usdPrice * quantity).toFixed(2),
          subtotal_local_currency: (localPrice * quantity).toFixed(2),
          is_gift: String(!!item.isGift),
          is_upgrade: String(!!item.isUpgrade),
          upgrade_from: String(item.upgradeFrom || ''),
          upgrade_source_order: String(item.upgradeFromOrder || ''),
          upgrade_source_order_id: String(item.upgradeFromOrderId || '')
        };
      });

      const upgradeSourceOrderId = (orderData.items || []).find(item => item.isUpgrade)?.upgradeFromOrderId;

      const { totalUSD, totalLocal } = items.reduce((totals, item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const usdPrice = parseFloat(item.unit_cost_usd) || 0;
        const localPrice = parseFloat(item.unit_cost_local_currency) || 0;
        return {
          totalUSD: totals.totalUSD + (usdPrice * quantity),
          totalLocal: totals.totalLocal + (localPrice * quantity)
        };
      }, { totalUSD: 0, totalLocal: 0 });

      const order = {
        code: String(orderNumber),
        orderDocRef: String(orderDocId),
        currency: String(currency),
        paymentPeriod: orderData.paymentPeriod ? String(orderData.paymentPeriod) : null,
        paymentPlan: orderData.paymentPlan || null,
        discountAmt: orderData.discountAmt || 0,
        customer: {
          name: String(orderData.customer?.name || ''),
          mobile: String(orderData.customer?.phone || ''),
          email: String(orderData.customer?.email || ''),
          billing_address: String(orderData.customer?.billingAddress || ''),
          shipping_address: String(orderData.customer?.shippingAddress || orderData.customer?.billingAddress || ''),
          country: currentCountry?.name || '',
          country_iso: currentCountry?.country_iso || ''
        },
        payment: orderData.payment || {
          method: orderData.paymentMethod === 'cash' ? 'Cash' : 'Payment Link',
          reference: orderData.paymentMethod === 'cash' ? 'CASH PAYMENT' : (orderData.customer?.paymentLink || 'https://be.plsss.vip/payment'),
          total_amount_usd: totalUSD.toFixed(2),
          amount_paid_usd: orderData.amountPaidUSD || '0.000',
          total_amount_local_currency: totalLocal.toFixed(2),
          amount_paid_local_currency: orderData.amountPaidLocal || '0.000',
          currency: String(currency),
          local_currency: String(currency),
          status: orderData.paymentMethod === 'cash' ? 'paid' : 'pending',
          timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19)
        },
        items,
        created_at: timestamp,
        updated_at: timestamp,
        items_count: String(items.length),
        subtotal_usd: totalUSD.toFixed(2),
        subtotal_local: totalLocal.toFixed(2),
        demo_booking_id: orderData.demoBookingId || '',
        gift_items_count: String((orderData.items || []).filter(item => item.isGift).length),
        agent_id: String(agentId),
        agent: orderData.agent ? {
          id: String(agentId),
          name: String(orderData.agent.name || ''),
          email: String(orderData.agent.email || '')
        } : null,
        ...(orderData.referrer && { referrer: String(orderData.referrer) })
      };

      const orderWithTimestamps = {
        ...order,
        orderNumber,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      if (orderData.demoBookingId) {
        await updateDemoBookingStatus(orderData.demoBookingId, 'completed');
      }

      const orderRef = doc(db, 'orders', orderDocId);
      await setDoc(orderRef, orderWithTimestamps);

      if (upgradeSourceOrderId) {
        try {
          const sourceOrderRef = doc(db, 'orders', upgradeSourceOrderId);
          await updateDoc(sourceOrderRef, {
            has_been_upgraded: true,
            upgraded_to_order_code: orderNumber,
            upgraded_to_order_id: orderDocId,
            upgraded_at: serverTimestamp()
          });
        } catch (updateErr) {
          console.error('Error marking original order as upgraded:', updateErr);
        }
      }

      return { success: true, orderNumber, id: orderDocId, ...order };
    } catch (error) {
      console.error('Error in createOrder:', error);
      throw error;
    }
  },

  async createDraftOrder(orderData, agentId) {
    try {
      const now = new Date();
      const timestamp = now.toISOString();
      const currency = orderData.currency || 'AED';
      const countryInfo = {
        'AED': { name: 'United Arab Emirates', country_iso: 'ARE', code: 'AED', flag: '🇦🇪', rate: '3.670', symbol: 'AED' },
        'BHD': { name: 'Bahrain', country_iso: 'BHR', code: 'BHD', flag: '🇧🇭', rate: '0.380', symbol: 'BHD' },
        'KWD': { name: 'Kuwait', country_iso: 'KWT', code: 'KWD', flag: '🇰🇼', rate: '0.310', symbol: 'KWD' },
        'OMR': { name: 'Oman', country_iso: 'OMN', code: 'OMR', flag: '🇴🇲', rate: '0.380', symbol: 'OMR' },
        'QAR': { name: 'Qatar', country_iso: 'QAT', code: 'QAR', flag: '🇶🇦', rate: '3.640', symbol: 'QAR' },
        'SAR': { name: 'Saudi Arabia', country_iso: 'SAU', code: 'SAR', flag: '🇸🇦', rate: '3.750', symbol: 'SAR' },
        'USD': { name: 'Philippines', country_iso: 'PHL', code: 'USD', flag: '🇵🇭', rate: '1.000', symbol: '$' }
      };
      const currentCountry = countryInfo[currency] || countryInfo['AED'];
      const agentCode = orderData.agent?.code || orderData.agent?.id || orderData.agentId || agentId;
      const draftOrderNumber = generateDraftOrderNumber(currentCountry.country_iso, agentCode);
      const draftOrderDocId = generateOrderDocId(draftOrderNumber);
      const customerLink = `https://be.plsss.vip/order/${draftOrderDocId}`;

      const requiredFields = ['customer', 'items', 'currency'];
      const missingFields = requiredFields.filter(field => !orderData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const items = (orderData.items || []).map(item => {
        const localPrice = item.price !== undefined ? parseFloat(item.price) : (item.prices ? parseFloat(item.prices[currency]) : 0);
        const usdPrice = item.prices ? parseFloat(item.prices.USD) : localPrice;
        const quantity = parseInt(item.quantity) || 1;
        return {
          name: String(item.name || ''),
          sku: String(item.id || ''),
          quantity: String(quantity),
          unit_cost_usd: usdPrice.toFixed(2),
          unit_cost_local_currency: localPrice.toFixed(2),
          subtotal_usd: (usdPrice * quantity).toFixed(2),
          subtotal_local_currency: (localPrice * quantity).toFixed(2),
          is_gift: String(!!item.isGift),
          is_upgrade: String(!!item.isUpgrade),
          upgrade_from: String(item.upgradeFrom || ''),
          upgrade_source_order: String(item.upgradeFromOrder || ''),
          upgrade_source_order_id: String(item.upgradeFromOrderId || '')
        };
      });

      const { totalUSD, totalLocal } = items.reduce((totals, item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const usdPrice = parseFloat(item.unit_cost_usd) || 0;
        const localPrice = parseFloat(item.unit_cost_local_currency) || 0;
        return {
          totalUSD: totals.totalUSD + (usdPrice * quantity),
          totalLocal: totals.totalLocal + (localPrice * quantity)
        };
      }, { totalUSD: 0, totalLocal: 0 });

      const draftOrder = {
        code: String(draftOrderNumber),
        currency: String(currency),
        paymentPeriod: orderData.paymentPeriod ? String(orderData.paymentPeriod) : null,
        paymentPlan: orderData.paymentPlan || null,
        discountAmt: orderData.discountAmt || 0,
        customer: {
          name: String(orderData.customer?.name || ''),
          mobile: String(orderData.customer?.phone || ''),
          email: String(orderData.customer?.email || ''),
          billing_address: String(orderData.customer?.billingAddress || ''),
          shipping_address: String(orderData.customer?.shippingAddress || orderData.customer?.billingAddress || ''),
          country: currentCountry?.name || '',
          country_iso: currentCountry?.country_iso || ''
        },
        payment: orderData.payment || {
          method: orderData.paymentMethod === 'cash' ? 'Cash' : 'Payment Link',
          reference: orderData.paymentMethod === 'cash' ? 'CASH PAYMENT' : (orderData.customer?.paymentLink || 'https://be.plsss.vip/payment'),
          total_amount_usd: totalUSD.toFixed(2),
          amount_paid_usd: orderData.amountPaidUSD || '0.000',
          total_amount_local_currency: totalLocal.toFixed(2),
          amount_paid_local_currency: orderData.amountPaidLocal || '0.000',
          currency: String(currency),
          local_currency: String(currency),
          status: orderData.paymentMethod === 'cash' ? 'paid' : 'pending',
          timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19)
        },
        items,
        created_at: timestamp,
        updated_at: timestamp,
        items_count: String(items.length),
        subtotal_usd: totalUSD.toFixed(2),
        subtotal_local: totalLocal.toFixed(2),
        demo_booking_id: orderData.demoBookingId || '',
        gift_items_count: String((orderData.items || []).filter(item => item.isGift).length),
        agent_id: String(agentId),
        agent: orderData.agent ? {
          id: String(agentId),
          name: String(orderData.agent.name || ''),
          email: String(orderData.agent.email || '')
        } : null,
        ...(orderData.referrer && { referrer: String(orderData.referrer) })
      };

      const draftData = {
        ...draftOrder,
        draftOrderNumber,
        customerLink,
        status: 'draft',
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        canIterate: true,
        version: 1,
        history: [
          {
            action: 'created',
            actor: 'agent',
            actorId: String(agentId),
            timestamp,
            notes: 'Draft order created'
          }
        ]
      };

      const draftOrderRef = doc(db, 'draft_orders', draftOrderDocId);
      await setDoc(draftOrderRef, draftData);

      return {
        success: true,
        draftOrderNumber,
        id: draftOrderDocId,
        customerLink,
        ...draftData
      };
    } catch (error) {
      console.error('Error in createDraftOrder:', error);
      throw error;
    }
  },

  async submitOrder(orderData) {
    try {
      const result = await this.createOrder(orderData);
      if (!result.success) throw new Error(result.error || 'Unknown error');
      return result;
    } catch (error) {
      console.error('Error in submitOrder:', error);
      throw error;
    }
  },

  async getOrdersByAgent(agentId) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('agentId', '==', agentId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const orders = [];
      querySnapshot.forEach((doc) => { orders.push({ id: doc.id, ...doc.data() }); });
      return orders;
    } catch (error) {
      console.error('Error getting orders by agent:', error);
      throw error;
    }
  },

  async getDraftOrdersByAgent(agentId) {
    try {
      const draftOrdersRef = collection(db, 'draft_orders');
      const q = query(draftOrdersRef, where('agent_id', '==', agentId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const draftOrders = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const now = new Date();
        const expires_at = data.expires_at ? new Date(data.expires_at) : null;
        draftOrders.push({
          id: doc.id,
          ...data,
          isExpired: expires_at && now > expires_at,
          expires_at: expires_at?.toISOString()
        });
      });
      return draftOrders;
    } catch (error) {
      console.error('Error getting draft orders by agent:', error);
      throw error;
    }
  },

  async updateDraftOrder(draftId, updates, agentId) {
    try {
      if (!draftId) throw new Error('Draft ID is required');
      const draftRef = doc(db, 'draft_orders', draftId);
      const draftSnap = await getDoc(draftRef);
      if (!draftSnap.exists()) throw new Error('Draft order not found');
      const draftData = draftSnap.data();
      if (draftData.agent_id !== agentId) throw new Error('Unauthorized');

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        canIterate: false,
        version: (draftData.version || 1) + 1,
        history: [
          ...(draftData.history || []),
          {
            action: 'updated',
            actor: 'agent',
            actorId: String(agentId),
            timestamp: new Date().toISOString(),
            notes: updates.notes || 'Draft order updated'
          }
        ]
      };

      await updateDoc(draftRef, updateData);
      return { success: true, id: draftId, ...updateData };
    } catch (error) {
      console.error('Error updating draft order:', error);
      throw error;
    }
  },

  async confirmDraftOrder(draftId, agentId, updatedPayment = null, userRole = 'agent') {
    try {
      return await runTransaction(db, async (transaction) => {
        const draftRef = doc(db, 'draft_orders', draftId);
        const draftSnap = await transaction.get(draftRef);
        if (!draftSnap.exists()) throw new Error('Draft order not found');
        const draftData = draftSnap.data();

        if (draftData.agent_id !== agentId && userRole !== 'admin') {
          throw new Error('Unauthorized');
        }

        const allowedStatuses = ['draft', 'sent', 'accepted', 'revision_requested'];
        if (!allowedStatuses.includes(draftData.status)) {
          throw new Error(`Cannot confirm draft with status: ${draftData.status}`);
        }

        const currency = draftData.currency || 'AED';
        const countryInfo = {
          'AED': { country_iso: 'ARE' },
          'BHD': { country_iso: 'BHR' },
          'KWD': { country_iso: 'KWT' },
          'OMR': { country_iso: 'OMN' },
          'QAR': { country_iso: 'QAT' },
          'SAR': { country_iso: 'SAU' },
          'USD': { country_iso: 'PHL' }
        };
        const countryIso = countryInfo[currency]?.country_iso || 'ARE';
        const orderNumber = await generateOrderNumber(countryIso);
        const orderDocId = generateOrderDocId(orderNumber);

        const finalOrderData = {
          ...draftData,
          code: String(orderNumber),
          orderNumber: String(orderNumber),
          orderDocRef: String(orderDocId),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          agent_id: String(draftData.agent_id || agentId),
          sourceDraftId: draftId,
          sourceDraftCode: draftData.draftOrderNumber || draftData.code || '',
          status: 'pending_fulfillment'
        };

        if (updatedPayment) {
          finalOrderData.payment = { ...finalOrderData.payment, ...updatedPayment, updatedAt: new Date().toISOString() };
        }

        delete finalOrderData.expires_at;
        delete finalOrderData.canIterate;
        delete finalOrderData.version;
        delete finalOrderData.customerLink;
        delete finalOrderData.draftOrderNumber;

        const orderRef = doc(db, 'orders', orderDocId);
        transaction.set(orderRef, finalOrderData);

        const draftUpdate = {
          status: 'converted',
          confirmedAt: serverTimestamp(),
          finalOrderId: orderDocId,
          finalOrderNumber: orderNumber,
          updatedAt: serverTimestamp(),
          history: [
            ...(draftData.history || []),
            {
              action: 'confirmed',
              actor: userRole === 'admin' && draftData.agent_id !== agentId ? 'admin' : 'agent',
              actorId: String(agentId),
              timestamp: new Date().toISOString(),
              notes: `Draft converted to official order ${orderNumber}${userRole === 'admin' && draftData.agent_id !== agentId ? ' (Admin Override)' : ''}`
            }
          ]
        };
        transaction.update(draftRef, draftUpdate);

        if (draftData.demo_booking_id) {
          transaction.update(doc(db, 'demo_bookings', draftData.demo_booking_id), {
            'payload.status': 'completed',
            'payload.updatedAt': serverTimestamp()
          });
        }

        const upgradeSourceOrderId = (draftData.items || []).find(item => item.is_upgrade === 'true' || item.isUpgrade)?.upgrade_source_order_id || (draftData.items || []).find(item => item.is_upgrade === 'true' || item.isUpgrade)?.upgradeFromOrderId;

        if (upgradeSourceOrderId) {
          transaction.update(doc(db, 'orders', upgradeSourceOrderId), {
            has_been_upgraded: true,
            upgraded_to_order_code: orderNumber,
            upgraded_to_order_id: orderDocId,
            upgraded_at: serverTimestamp()
          });
        }

        return { success: true, orderNumber, orderId: orderDocId, draftId };
      });
    } catch (error) {
      console.error('Error confirming draft order:', error);
      throw error;
    }
  },

  async cancelDraftOrder(draftId, agentId, reason = 'Cancelled by agent') {
    try {
      const draftRef = doc(db, 'draft_orders', draftId);
      const draftSnap = await getDoc(draftRef);
      if (!draftSnap.exists()) throw new Error('Draft order not found');
      const draftData = draftSnap.data();
      if (draftData.agent_id !== agentId) throw new Error('Unauthorized');

      const updateData = {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelReason: reason,
        updatedAt: serverTimestamp(),
        history: [
          ...(draftData.history || []),
          {
            action: 'cancelled',
            actor: 'agent',
            actorId: String(agentId),
            timestamp: new Date().toISOString(),
            notes: reason
          }
        ]
      };
      await updateDoc(draftRef, updateData);
      return { success: true, id: draftId, ...updateData };
    } catch (error) {
      console.error('Error cancelling draft order:', error);
      throw error;
    }
  },

  async getDraftOrder(draftId) {
    try {
      const draftRef = doc(db, 'draft_orders', draftId);
      const draftSnap = await getDoc(draftRef);
      if (!draftSnap.exists()) throw new Error('Draft order not found');
      const data = draftSnap.data();
      const now = new Date();
      const expires_at = data.expires_at ? new Date(data.expires_at) : null;
      return { id: draftSnap.id, ...data, isExpired: expires_at && now > expires_at, expires_at: expires_at?.toISOString() };
    } catch (error) {
      console.error('Error getting draft order:', error);
      throw error;
    }
  }
};

export const agentService = {
  async getAgents() {
    try {
      const agentsRef = collection(db, 'agents');
      const querySnapshot = await getDocs(agentsRef);
      const agents = [];
      querySnapshot.forEach((doc) => { agents.push({ id: doc.id, ...doc.data() }); });
      return agents;
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  async validateAgent(email) {
    try {
      const agentsRef = collection(db, 'agents');
      const q = query(agentsRef, where('email', '==', email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return { valid: false, message: 'No agent found' };
      const agentData = querySnapshot.docs[0].data();
      return { valid: true, agent: { id: querySnapshot.docs[0].id, ...agentData } };
    } catch (error) {
      console.error('Error validating agent:', error);
      throw error;
    }
  }
};

export const demoService = {
  async bookDemo(demoData) {
    try {
      const demosRef = collection(db, 'demos');
      const docRef = await addDoc(demosRef, { ...demoData, status: 'scheduled', createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error booking demo:', error);
      throw error;
    }
  },

  async getDemosByAgent(agentId) {
    try {
      const demosRef = collection(db, 'demos');
      const q = query(demosRef, where('agentId', '==', agentId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const demos = [];
      querySnapshot.forEach((doc) => { demos.push({ id: doc.id, ...doc.data() }); });
      return demos;
    } catch (error) {
      console.error('Error getting demos by agent:', error);
      throw error;
    }
  }
};
